import { useState } from "react";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";

const BLANK_WINDOW = {
  ref: "",
  blindType: "",
  width: "",
  drop: "",
  unit: "mm",
  controlSide: "",
  controlType: "",
  material: "",
  colour: "",
  qty: 1,
  sizeType: "Recess",
  price: "",
  notes: "",
};

const uid = () => Math.random().toString(36).slice(2, 9);

const inputCls =
  "rounded-lg border border-warm-300 px-2 py-2 text-xs focus:border-navy-900 focus:outline-none w-full min-w-16";

export function MeasurementSheet({ lead, api, onSaved }) {
  const [rooms, setRooms] = useState(
    lead.measurement_rooms?.length
      ? lead.measurement_rooms
      : [{ id: uid(), name: "Room 1", windows: [{ id: uid(), ...BLANK_WINDOW }] }],
  );
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const setRoomName = (ri, name) =>
    setRooms((rs) => rs.map((r, i) => (i === ri ? { ...r, name } : r)));
  const addRoom = () =>
    setRooms((rs) => [...rs, { id: uid(), name: `Room ${rs.length + 1}`, windows: [{ id: uid(), ...BLANK_WINDOW }] }]);
  const removeRoom = (ri) => setRooms((rs) => rs.filter((_, i) => i !== ri));
  const addWindow = (ri) =>
    setRooms((rs) => rs.map((r, i) => (i === ri ? { ...r, windows: [...r.windows, { id: uid(), ...BLANK_WINDOW }] } : r)));
  const dupWindow = (ri, wi) =>
    setRooms((rs) =>
      rs.map((r, i) => {
        if (i !== ri) return r;
        const wins = [...r.windows];
        wins.splice(wi + 1, 0, { ...wins[wi], id: uid() });
        return { ...r, windows: wins };
      }),
    );
  const delWindow = (ri, wi) =>
    setRooms((rs) =>
      rs.map((r, i) => (i === ri ? { ...r, windows: r.windows.filter((_, x) => x !== wi) } : r)),
    );
  const setWin = (ri, wi, k, v) =>
    setRooms((rs) =>
      rs.map((r, i) =>
        i === ri
          ? { ...r, windows: r.windows.map((w, x) => (x === wi ? { ...w, [k]: v } : w)) }
          : r,
      ),
    );

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/leads/${lead.id}/measurements`, { rooms });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6" data-testid="measurement-sheet">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
        Measurement sheet
      </h3>
      {rooms.map((room, ri) => (
        <div key={room.id} className="rounded-xl border border-warm-200 mb-3 overflow-hidden">
          <div className="flex items-center gap-2 bg-warm-100 px-3 py-2">
            <input
              value={room.name}
              onChange={(e) => setRoomName(ri, e.target.value)}
              aria-label="Room name"
              className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none flex-1"
            />
            <button type="button" onClick={() => addWindow(ri)} data-testid={`add-window-${ri}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy-900">
              <Plus className="h-3 w-3" /> Window
            </button>
            {rooms.length > 1 && (
              <button type="button" onClick={() => removeRoom(ri)} aria-label="Remove room" className="text-slate-400 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-warm-200">
                  {["Ref", "Blind type", "Width", "Drop", "Unit", "Control", "Fabric/Colour", "Qty", "Recess/Exact", "£ Price", ""].map((h) => (
                    <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {room.windows.map((w, wi) => (
                  <tr key={w.id} className="border-b border-warm-200/60 last:border-0">
                    <td className="p-1.5"><input value={w.ref} onChange={(e) => setWin(ri, wi, "ref", e.target.value)} placeholder="W1" className={inputCls} style={{ minWidth: 48 }} /></td>
                    <td className="p-1.5">
                      <select value={w.blindType} onChange={(e) => setWin(ri, wi, "blindType", e.target.value)} className={inputCls} aria-label="Blind type">
                        <option value="">Select…</option>
                        {["Roller", "Roman", "Venetian", "Wooden Venetian", "Vertical", "Day & Night", "Perfect Fit", "Motorised", "Skylight", "Blackout", "Shutters", "Other"].map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5"><input value={w.width} onChange={(e) => setWin(ri, wi, "width", e.target.value)} type="number" className={inputCls} style={{ minWidth: 64 }} aria-label="Width" /></td>
                    <td className="p-1.5"><input value={w.drop} onChange={(e) => setWin(ri, wi, "drop", e.target.value)} type="number" className={inputCls} style={{ minWidth: 64 }} aria-label="Drop" /></td>
                    <td className="p-1.5">
                      <select value={w.unit} onChange={(e) => setWin(ri, wi, "unit", e.target.value)} className={inputCls} aria-label="Unit">
                        <option>mm</option><option>cm</option><option>in</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      <select value={w.controlSide} onChange={(e) => setWin(ri, wi, "controlSide", e.target.value)} className={inputCls} aria-label="Control side">
                        <option value="">—</option><option>Left</option><option>Right</option><option>Motor</option>
                      </select>
                    </td>
                    <td className="p-1.5"><input value={w.material} onChange={(e) => setWin(ri, wi, "material", e.target.value)} placeholder="Fabric / colour" className={inputCls} style={{ minWidth: 100 }} /></td>
                    <td className="p-1.5"><input value={w.qty} onChange={(e) => setWin(ri, wi, "qty", e.target.value)} type="number" min="1" className={inputCls} style={{ minWidth: 48 }} aria-label="Quantity" /></td>
                    <td className="p-1.5">
                      <select value={w.sizeType} onChange={(e) => setWin(ri, wi, "sizeType", e.target.value)} className={inputCls} aria-label="Recess or exact">
                        <option>Recess</option><option>Exact</option>
                      </select>
                    </td>
                    <td className="p-1.5"><input value={w.price} onChange={(e) => setWin(ri, wi, "price", e.target.value)} type="number" min="0" step="0.01" className={inputCls} style={{ minWidth: 72 }} aria-label="Price" /></td>
                    <td className="p-1.5 whitespace-nowrap">
                      <button type="button" onClick={() => dupWindow(ri, wi)} aria-label="Duplicate window" className="text-slate-400 hover:text-navy-900 mr-1.5"><Copy className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => delWindow(ri, wi)} aria-label="Delete window" className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button type="button" onClick={addRoom} data-testid="add-room" className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-900 hover:text-navy-800">
          <Plus className="h-3.5 w-3.5" /> Add Room
        </button>
        <button
          type="button"
          data-testid="measurements-save"
          onClick={save}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-navy-900 px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-800 disabled:opacity-40"
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {saved ? "Saved ✓" : "Save Measurements"}
        </button>
      </div>
    </div>
  );
}
